import React from 'react';
import './AbsorptionWidget.css';

/**
 * AbsorptionWidget - Main container for absorption model visualizations
 *
 * Widget Types:
 * - single_prediction: Speedometer + SHAP + Survival curve
 * - price_sensitivity: Price vs absorption curve
 * - portfolio_simulation: Timeline fan chart + lot outcomes
 */

// =============================================================================
// SPEEDOMETER GAUGE - The headline "how fast will it sell"
// =============================================================================
const SpeedometerGauge = ({ expectedMonths, p10, p90, maxMonths = 12 }) => {
  // Clamp values
  const value = Math.min(Math.max(expectedMonths, 0), maxMonths);
  const p10Clamped = Math.min(Math.max(p10, 0), maxMonths);
  const p90Clamped = Math.min(Math.max(p90, 0), maxMonths);

  // Convert months to angle (0-180 degrees, left to right)
  const valueToAngle = (v) => (v / maxMonths) * 180;
  const needleAngle = valueToAngle(value);
  const p10Angle = valueToAngle(p10Clamped);
  const p90Angle = valueToAngle(p90Clamped);

  // Color stops for the arc (green = fast, red = slow)
  const getSpeedLabel = () => {
    if (value <= 2) return { label: 'Very Fast', color: '#10b981' };
    if (value <= 4) return { label: 'Fast', color: '#34d399' };
    if (value <= 6) return { label: 'Average', color: '#fbbf24' };
    if (value <= 9) return { label: 'Slow', color: '#f97316' };
    return { label: 'Very Slow', color: '#ef4444' };
  };

  const speed = getSpeedLabel();

  // SVG arc path
  const describeArc = (x, y, radius, startAngle, endAngle) => {
    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`;
  };

  const polarToCartesian = (cx, cy, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees - 180) * Math.PI / 180;
    return {
      x: cx + (radius * Math.cos(angleInRadians)),
      y: cy + (radius * Math.sin(angleInRadians))
    };
  };

  return (
    <div className="speedometer-container">
      <svg viewBox="0 0 200 120" className="speedometer-svg">
        {/* Background arc */}
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10b981" />
            <stop offset="33%" stopColor="#34d399" />
            <stop offset="50%" stopColor="#fbbf24" />
            <stop offset="75%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
        </defs>

        {/* Main arc background */}
        <path
          d={describeArc(100, 100, 80, 0, 180)}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="16"
          strokeLinecap="round"
        />

        {/* Colored arc */}
        <path
          d={describeArc(100, 100, 80, 0, 180)}
          fill="none"
          stroke="url(#gaugeGradient)"
          strokeWidth="16"
          strokeLinecap="round"
        />

        {/* Confidence interval arc (darker overlay) */}
        <path
          d={describeArc(100, 100, 80, p10Angle, p90Angle)}
          fill="none"
          stroke="rgba(0,0,0,0.2)"
          strokeWidth="20"
          strokeLinecap="round"
        />

        {/* Tick marks */}
        {[0, 3, 6, 9, 12].map((month, i) => {
          const angle = valueToAngle(month);
          const innerPoint = polarToCartesian(100, 100, 65, angle);
          const outerPoint = polarToCartesian(100, 100, 72, angle);
          return (
            <g key={month}>
              <line
                x1={innerPoint.x}
                y1={innerPoint.y}
                x2={outerPoint.x}
                y2={outerPoint.y}
                stroke="#9ca3af"
                strokeWidth="2"
              />
              <text
                x={polarToCartesian(100, 100, 56, angle).x}
                y={polarToCartesian(100, 100, 56, angle).y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="speedometer-tick-label"
              >
                {month}
              </text>
            </g>
          );
        })}

        {/* Needle */}
        <g transform={`rotate(${needleAngle - 90}, 100, 100)`}>
          <polygon
            points="100,25 95,100 105,100"
            fill="#1f2937"
          />
          <circle cx="100" cy="100" r="8" fill="#1f2937" />
          <circle cx="100" cy="100" r="4" fill="#ffffff" />
        </g>
      </svg>

      {/* Value display */}
      <div className="speedometer-value">
        <span className="speedometer-number">{value.toFixed(1)}</span>
        <span className="speedometer-unit">months</span>
      </div>

      {/* Speed label */}
      <div className="speedometer-label" style={{ color: speed.color }}>
        {speed.label}
      </div>

      {/* Confidence range */}
      <div className="speedometer-range">
        <span className="range-optimistic">{p10.toFixed(1)}mo</span>
        <span className="range-separator">to</span>
        <span className="range-pessimistic">{p90.toFixed(1)}mo</span>
      </div>
    </div>
  );
};

// =============================================================================
// SURVIVAL CURVE - Probability over time
// =============================================================================
const SurvivalCurve = ({ survivalCurve, milestones }) => {
  if (!survivalCurve || survivalCurve.length === 0) return null;

  const width = 300;
  const height = 160;
  const padding = { top: 20, right: 20, bottom: 30, left: 40 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Scale functions
  const xScale = (month) => padding.left + (month / (survivalCurve.length - 1)) * chartWidth;
  const yScale = (prob) => padding.top + (1 - prob) * chartHeight;

  // Build path for sold probability (cumulative)
  const soldPath = survivalCurve.map((point, i) => {
    const x = xScale(point.month);
    const y = yScale(point.sold);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  // Build area fill path
  const areaPath = soldPath +
    ` L ${xScale(survivalCurve[survivalCurve.length - 1].month)} ${yScale(0)}` +
    ` L ${xScale(0)} ${yScale(0)} Z`;

  return (
    <div className="survival-curve-container">
      <div className="survival-curve-title">Probability of Sale Over Time</div>

      <svg viewBox={`0 0 ${width} ${height}`} className="survival-curve-svg">
        <defs>
          <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#10b981" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1.0].map(prob => (
          <line
            key={prob}
            x1={padding.left}
            y1={yScale(prob)}
            x2={width - padding.right}
            y2={yScale(prob)}
            stroke="#e5e7eb"
            strokeDasharray="4,4"
          />
        ))}

        {/* Area fill */}
        <path d={areaPath} fill="url(#areaGradient)" />

        {/* Line */}
        <path
          d={soldPath}
          fill="none"
          stroke="#10b981"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {survivalCurve.map((point, i) => (
          <circle
            key={i}
            cx={xScale(point.month)}
            cy={yScale(point.sold)}
            r="4"
            fill="#ffffff"
            stroke="#10b981"
            strokeWidth="2"
          />
        ))}

        {/* X-axis labels */}
        {survivalCurve.filter((_, i) => i % 2 === 0 || i === survivalCurve.length - 1).map((point) => (
          <text
            key={point.month}
            x={xScale(point.month)}
            y={height - 8}
            textAnchor="middle"
            className="survival-axis-label"
          >
            {point.month}mo
          </text>
        ))}

        {/* Y-axis labels */}
        {[0, 0.5, 1.0].map(prob => (
          <text
            key={prob}
            x={padding.left - 8}
            y={yScale(prob)}
            textAnchor="end"
            dominantBaseline="middle"
            className="survival-axis-label"
          >
            {(prob * 100).toFixed(0)}%
          </text>
        ))}
      </svg>

      {/* Milestone callouts */}
      {milestones && (
        <div className="survival-milestones">
          {milestones['30_days'] !== undefined && (
            <div className="milestone">
              <span className="milestone-value">{(milestones['30_days'] * 100).toFixed(0)}%</span>
              <span className="milestone-label">by 30 days</span>
            </div>
          )}
          {milestones['60_days'] !== undefined && (
            <div className="milestone">
              <span className="milestone-value">{(milestones['60_days'] * 100).toFixed(0)}%</span>
              <span className="milestone-label">by 60 days</span>
            </div>
          )}
          {milestones['90_days'] !== undefined && (
            <div className="milestone">
              <span className="milestone-value">{(milestones['90_days'] * 100).toFixed(0)}%</span>
              <span className="milestone-label">by 90 days</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// SHAP WATERFALL - Why this prediction
// =============================================================================
const ShapWaterfall = ({ factors, baselineMonths, predictedMonths }) => {
  if (!factors) return null;

  const { helping = [], hurting = [] } = factors;

  // Combine and sort by absolute impact
  const allFactors = [
    ...helping.map(f => ({ ...f, direction: 'helping' })),
    ...hurting.map(f => ({ ...f, direction: 'hurting' }))
  ];

  if (allFactors.length === 0) return null;

  return (
    <div className="shap-waterfall-container">
      <div className="shap-title">Key Factors</div>

      <div className="shap-factors">
        {/* Helping factors (green) */}
        {helping.length > 0 && (
          <div className="shap-section shap-helping">
            <div className="shap-section-header">
              <span className="shap-icon">↑</span>
              <span>Helping (Faster Sale)</span>
            </div>
            {helping.map((factor, i) => (
              <div key={i} className="shap-factor">
                <div className="shap-factor-name">{factor.factor}</div>
                <div className="shap-factor-detail">{factor.detail}</div>
                <div className={`shap-factor-impact impact-${factor.impact}`}>
                  {factor.impact}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Hurting factors (orange/red) */}
        {hurting.length > 0 && (
          <div className="shap-section shap-hurting">
            <div className="shap-section-header">
              <span className="shap-icon">↓</span>
              <span>Hurting (Slower Sale)</span>
            </div>
            {hurting.map((factor, i) => (
              <div key={i} className="shap-factor">
                <div className="shap-factor-name">{factor.factor}</div>
                <div className="shap-factor-detail">{factor.detail}</div>
                <div className={`shap-factor-impact impact-${factor.impact}`}>
                  {factor.impact}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// DATA QUALITY BADGE - Confidence indicator
// =============================================================================
const DataQualityBadge = ({ confidence = 'unknown', warnings = [] }) => {
  const getConfidenceDisplay = () => {
    switch (confidence) {
      case 'high':
        return { color: '#10b981', icon: '●', label: 'High Confidence' };
      case 'medium':
        return { color: '#fbbf24', icon: '●', label: 'Medium Confidence' };
      case 'low':
        return { color: '#ef4444', icon: '●', label: 'Low Confidence' };
      case 'unknown':
        return { color: '#9ca3af', icon: '○', label: 'Confidence Unknown' };
      default:
        return { color: '#9ca3af', icon: '○', label: 'Unknown' };
    }
  };

  const display = getConfidenceDisplay();

  return (
    <div className="data-quality-badge" style={{ borderColor: display.color }}>
      <span className="quality-icon" style={{ color: display.color }}>{display.icon}</span>
      <span className="quality-label">{display.label}</span>
      {warnings.length > 0 && (
        <span className="quality-warning" title={warnings.join(', ')}>⚠</span>
      )}
    </div>
  );
};

// =============================================================================
// RELATIVE POSITION - Market context
// =============================================================================
const RelativePosition = ({ position }) => {
  if (!position || (!position.ranking && !position.vs_median)) return null;

  return (
    <div className="relative-position">
      {position.ranking ? <div className="position-ranking">{position.ranking}</div> : null}
      {position.vs_median ? <div className="position-vs-median">{position.vs_median}</div> : null}
    </div>
  );
};

// =============================================================================
// PRICE SENSITIVITY CURVE - Price vs absorption
// =============================================================================
const PriceSensitivityCurve = ({ sensitivityCurve, optimalPrice, recommendations }) => {
  if (!sensitivityCurve || sensitivityCurve.length === 0) return null;

  const width = 400;
  const height = 200;
  const padding = { top: 30, right: 30, bottom: 50, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Get data ranges
  const prices = sensitivityCurve.map(p => p.price);
  const months = sensitivityCurve.map(p => p.expected_months);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const minMonths = Math.min(...months);
  const maxMonths = Math.max(...months);

  // Scale functions
  const xScale = (price) => padding.left + ((price - minPrice) / (maxPrice - minPrice)) * chartWidth;
  const yScale = (month) => padding.top + ((maxMonths - month) / (maxMonths - minMonths)) * chartHeight;

  // Build path
  const linePath = sensitivityCurve.map((point, i) => {
    const x = xScale(point.price);
    const y = yScale(point.expected_months);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  // Format price for display
  const formatPrice = (price) => `$${(price / 1000).toFixed(0)}K`;

  return (
    <div className="price-sensitivity-container">
      <div className="price-sensitivity-title">Price vs. Time to Sale</div>

      <svg viewBox={`0 0 ${width} ${height}`} className="price-sensitivity-svg">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map(pct => {
          const y = padding.top + pct * chartHeight;
          const month = maxMonths - pct * (maxMonths - minMonths);
          return (
            <g key={pct}>
              <line
                x1={padding.left}
                y1={y}
                x2={width - padding.right}
                y2={y}
                stroke="#e5e7eb"
                strokeDasharray="4,4"
              />
              <text
                x={padding.left - 8}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                className="axis-label"
              >
                {month.toFixed(1)}mo
              </text>
            </g>
          );
        })}

        {/* Optimal price line */}
        {optimalPrice && (
          <g>
            <line
              x1={xScale(optimalPrice)}
              y1={padding.top}
              x2={xScale(optimalPrice)}
              y2={height - padding.bottom}
              stroke="#10b981"
              strokeWidth="2"
              strokeDasharray="6,4"
            />
            <text
              x={xScale(optimalPrice)}
              y={padding.top - 8}
              textAnchor="middle"
              className="optimal-label"
            >
              Optimal: {formatPrice(optimalPrice)}
            </text>
          </g>
        )}

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke="#3b82f6"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {sensitivityCurve.map((point, i) => (
          <g key={i}>
            <circle
              cx={xScale(point.price)}
              cy={yScale(point.expected_months)}
              r="5"
              fill={point.price === optimalPrice ? '#10b981' : '#ffffff'}
              stroke={point.price === optimalPrice ? '#10b981' : '#3b82f6'}
              strokeWidth="2"
            />
          </g>
        ))}

        {/* X-axis labels */}
        {sensitivityCurve.filter((_, i) => i % 2 === 0 || i === sensitivityCurve.length - 1).map((point, i) => (
          <text
            key={i}
            x={xScale(point.price)}
            y={height - padding.bottom + 20}
            textAnchor="middle"
            className="axis-label"
          >
            {formatPrice(point.price)}
          </text>
        ))}

        {/* Axis labels */}
        <text
          x={width / 2}
          y={height - 5}
          textAnchor="middle"
          className="axis-title"
        >
          List Price
        </text>
        <text
          x={15}
          y={height / 2}
          textAnchor="middle"
          transform={`rotate(-90, 15, ${height / 2})`}
          className="axis-title"
        >
          Months to Sale
        </text>
      </svg>

      {/* Recommendations */}
      {recommendations && (
        <div className="price-recommendations">
          <div className="recommendation-item">
            <span className="rec-label">Recommended Price:</span>
            <span className="rec-value">{formatPrice(recommendations.optimal_price)}</span>
          </div>
          <div className="recommendation-detail">{recommendations.reasoning}</div>
          {recommendations.price_elasticity && (
            <div className="recommendation-elasticity">{recommendations.price_elasticity}</div>
          )}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// LOT OUTCOMES TABLE - Portfolio simulation results
// =============================================================================
const LotOutcomesTable = ({ lotOutcomes, portfolioSummary }) => {
  if (!lotOutcomes || lotOutcomes.length === 0) return null;

  const getProbabilityColor = (prob) => {
    if (prob >= 0.8) return '#10b981';
    if (prob >= 0.6) return '#34d399';
    if (prob >= 0.4) return '#fbbf24';
    return '#ef4444';
  };

  return (
    <div className="lot-outcomes-container">
      <div className="lot-outcomes-title">Lot-by-Lot Analysis</div>

      <div className="lot-outcomes-table">
        <div className="lot-table-header">
          <div className="lot-col lot-id">Lot</div>
          <div className="lot-col lot-price">Price</div>
          <div className="lot-col lot-release">Release</div>
          <div className="lot-col lot-prob">Sale Prob.</div>
          <div className="lot-col lot-months">Expected</div>
        </div>

        {lotOutcomes.map((lot, i) => (
          <div key={i} className="lot-table-row">
            <div className="lot-col lot-id">{lot.lot_id}</div>
            <div className="lot-col lot-price">${(lot.price / 1000).toFixed(0)}K</div>
            <div className="lot-col lot-release">Month {lot.release_month}</div>
            <div className="lot-col lot-prob">
              <div
                className="prob-bar"
                style={{
                  width: `${lot.sale_probability * 100}%`,
                  backgroundColor: getProbabilityColor(lot.sale_probability)
                }}
              />
              <span className="prob-value">{(lot.sale_probability * 100).toFixed(0)}%</span>
            </div>
            <div className="lot-col lot-months">
              {lot.expected_months_to_sale ? `${lot.expected_months_to_sale.toFixed(1)}mo` : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Portfolio Summary */}
      {portfolioSummary && (
        <div className="portfolio-summary">
          <div className="summary-item">
            <span className="summary-label">Expected Sales</span>
            <span className="summary-value">{portfolioSummary.expected_sales.toFixed(1)} / {portfolioSummary.total_lots}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Expected Revenue</span>
            <span className="summary-value">${(portfolioSummary.expected_revenue / 1000000).toFixed(2)}M</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Avg. Time to Sale</span>
            <span className="summary-value">{portfolioSummary.weighted_avg_months.toFixed(1)} months</span>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// RECOMMENDATIONS LIST
// =============================================================================
const RecommendationsList = ({ recommendations }) => {
  if (!recommendations || recommendations.length === 0) return null;

  return (
    <div className="recommendations-container">
      <div className="recommendations-title">Recommendations</div>
      <ul className="recommendations-list">
        {recommendations.map((rec, i) => (
          <li key={i} className="recommendation-item">{rec}</li>
        ))}
      </ul>
    </div>
  );
};

// =============================================================================
// MAIN ABSORPTION WIDGET
// =============================================================================
const AbsorptionWidget = ({ data, widgetType }) => {
  if (!data) return null;

  // Determine widget type from data or prop
  const type = widgetType || data.widget_type || 'single_prediction';

  return (
    <div className="absorption-widget">
      {/* Single Prediction Widget */}
      {type === 'single_prediction' && (
        <div className="absorption-single-prediction">
          <div className="absorption-header">
            <h3 className="absorption-title">Absorption Forecast</h3>
            <DataQualityBadge
              confidence={data.data_quality?.confidence}
              warnings={data.data_quality?.warnings}
            />
          </div>

          <div className="absorption-main">
            {/* Left: Speedometer */}
            <div className="absorption-gauge-section">
              <SpeedometerGauge
                expectedMonths={data.prediction?.expected_months_to_sale || 3}
                p10={data.prediction?.confidence_interval?.p10 || 1}
                p90={data.prediction?.confidence_interval?.p90 || 6}
              />
              <RelativePosition position={data.relative_position} />
            </div>

            {/* Right: Key Factors */}
            <div className="absorption-factors-section">
              <ShapWaterfall factors={data.key_factors} />
            </div>
          </div>

          {/* Bottom: Survival Curve */}
          <div className="absorption-curve-section">
            <SurvivalCurve
              survivalCurve={data.survival_curve}
              milestones={data.prediction?.sale_probability}
            />
          </div>
        </div>
      )}

      {/* Price Sensitivity Widget */}
      {type === 'price_sensitivity' && (
        <div className="absorption-price-sensitivity">
          <div className="absorption-header">
            <h3 className="absorption-title">Price Optimization</h3>
            <DataQualityBadge confidence="high" />
          </div>

          <PriceSensitivityCurve
            sensitivityCurve={data.sensitivity_curve}
            optimalPrice={data.recommendations?.optimal_price}
            recommendations={data.recommendations}
          />

          {data.market_context && (
            <div className="market-context">
              <span>Market Median: ${(data.market_context.zip_median / 1000).toFixed(0)}K</span>
              <span>{data.market_context.note}</span>
            </div>
          )}
        </div>
      )}

      {/* Portfolio Simulation Widget */}
      {type === 'portfolio_simulation' && (
        <div className="absorption-portfolio-simulation">
          <div className="absorption-header">
            <h3 className="absorption-title">Portfolio Simulation</h3>
            <div className="simulation-id">ID: {data.simulation_id}</div>
          </div>

          <LotOutcomesTable
            lotOutcomes={data.lot_outcomes}
            portfolioSummary={data.portfolio_summary}
          />

          <RecommendationsList recommendations={data.recommendations} />
        </div>
      )}
    </div>
  );
};

export default AbsorptionWidget;
